
import React from 'react';
import { Card } from "@/components/ui/card";
import { Grid3X3 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PerformanceData } from './types';

interface TopicHeatmapChartProps {
  performanceData: PerformanceData[];
}

export const TopicHeatmapChart: React.FC<TopicHeatmapChartProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const topicData = React.useMemo(() => {
    const data: Record<string, Record<string, number>> = {};
    
    performanceData.forEach(entry => {
      if (!data[entry.subject]) {
        data[entry.subject] = {};
      }
      if (!data[entry.subject][entry.difficulty]) {
        data[entry.subject][entry.difficulty] = 0;
      }
      const accuracy = (entry.correct_answers / entry.total_questions) * 100;
      data[entry.subject][entry.difficulty] = accuracy;
    });

    return data;
  }, [performanceData]);

  const getColorForAccuracy = (accuracy: number) => {
    if (accuracy >= 80) return 'bg-green-500';
    if (accuracy >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const difficulties = ['easy', 'medium', 'hard'];

  return (
    <Card className={isMobile ? "p-3" : "p-6"}>
      <h2 className={`${isMobile ? "text-base" : "text-xl"} font-semibold mb-4 flex items-center gap-2`}>
        <Grid3X3 className="w-5 h-5 text-purple-500" />
        Topic-Wise Accuracy Heatmap
      </h2>
      <div className="overflow-x-auto -mx-3">
        <div className={`${isMobile ? "px-3 pb-1 min-w-[280px]" : ""} w-full`}>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${isMobile ? "p-1 text-xs" : "p-2"} border text-left font-medium bg-gray-50`}>Subject</th>
                {difficulties.map(diff => (
                  <th key={diff} className={`${isMobile ? "p-1 text-xs" : "p-2 text-sm"} border font-medium text-center bg-gray-50`}>
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(topicData).map(([subject, difficultyScores], idx) => (
                <tr key={subject} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className={`${isMobile ? "p-1 text-xs" : "p-2"} border font-medium text-left`}>{subject}</td>
                  {difficulties.map(diff => (
                    <td key={`${subject}-${diff}`} className={`${isMobile ? "p-1" : "p-2"} border`}>
                      <div className="relative">
                        <div
                          className={`w-full ${isMobile ? "h-6" : "h-8"} ${getColorForAccuracy(difficultyScores[diff] || 0)} opacity-50 rounded`}
                        ></div>
                        <div className={`absolute inset-0 flex items-center justify-center ${isMobile ? "text-xs font-medium" : "text-sm font-medium"}`}>
                          {difficultyScores[diff] ? `${Math.round(difficultyScores[diff])}%` : 'N/A'}
                        </div>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className={`mt-2 ${isMobile ? "text-xs" : "text-sm"} text-gray-500`}>
        Showing subject performance across different difficulty levels
      </div>
    </Card>
  );
};
